import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LogoImage } from "@/components/Logo";

const pwInputClass =
  "w-full bg-[#141414] border border-white/15 px-5 py-4 text-sm text-[#F5F5F5] placeholder:text-[#5b5b63] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300";
const pwLabelClass = "block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3";

const ChangePasswordPanel = () => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
        confirm_password: form.confirm,
      });
      toast.success("Password updated — other sessions have been signed out.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-14 border border-white/10 bg-[#0E0E0E] p-8 sm:p-12 max-w-xl" data-testid="admin-change-password-panel">
      <h2 className="font-serif-display text-2xl mb-2">
        Change <em className="italic text-[#D4AF37]">password</em>
      </h2>
      <p className="text-xs text-[#5b5b63] mb-8">
        At least 12 characters. All other sessions are signed out after a change.
      </p>
      <form onSubmit={submit} className="space-y-6">
        <div>
          <label htmlFor="cp-current" className={pwLabelClass}>Current password</label>
          <input id="cp-current" data-testid="admin-change-password-current" type="password" required autoComplete="current-password" value={form.current} onChange={set("current")} className={pwInputClass} maxLength={200} />
        </div>
        <div>
          <label htmlFor="cp-new" className={pwLabelClass}>New password</label>
          <input id="cp-new" data-testid="admin-change-password-new" type="password" required autoComplete="new-password" value={form.next} onChange={set("next")} className={pwInputClass} maxLength={200} />
        </div>
        <div>
          <label htmlFor="cp-confirm" className={pwLabelClass}>Confirm new password</label>
          <input id="cp-confirm" data-testid="admin-change-password-confirm" type="password" required autoComplete="new-password" value={form.confirm} onChange={set("confirm")} className={pwInputClass} maxLength={200} />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-change-password-submit"
          className="bg-[#D4AF37] text-black text-xs tracking-[0.3em] uppercase font-semibold px-10 py-4 hover:bg-[#C5A24D] disabled:opacity-60 transition-colors duration-300"
        >
          {saving ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
};

export default function AdminSubmissions() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("active");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api
        .get("/admin/submissions")
        .then(({ data }) => setData(data))
        .catch((e) => setError(e.response?.data?.detail || "Failed to load submissions"));
    }
  }, [user]);

  const signOut = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const exportCsv = async () => {
    const res = await api.get("/admin/submissions/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "she-global-interest-submissions.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const patchSubmission = async (s, updates, successMsg) => {
    try {
      await api.patch(`/admin/submissions/${s.id}`, updates);
      setData({
        ...data,
        submissions: data.submissions.map((x) => (x.id === s.id ? { ...x, ...updates } : x)),
      });
      toast.success(successMsg);
    } catch {
      toast.error("Could not update the submission");
    }
  };

  const toggleContacted = (s) =>
    patchSubmission(s, { contacted: !s.contacted }, s.contacted ? `Marked ${s.name} as new` : `Marked ${s.name} as contacted`);

  const toggleArchived = (s) =>
    patchSubmission(s, { archived: !s.archived }, s.archived ? `Restored ${s.name}` : `Archived ${s.name}`);

  const remove = async (s) => {
    if (!window.confirm(`Delete the submission from ${s.name} (${s.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/submissions/${s.id}`);
      setData({
        count: data.count - 1,
        submissions: data.submissions.filter((x) => x.id !== s.id),
      });
      toast.success(`Deleted submission from ${s.name}`);
    } catch {
      toast.error("Could not delete the submission");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="admin-loading">
        <span className="text-[0.65rem] tracking-[0.4em] uppercase text-white animate-pulse">Verifying access…</span>
      </div>
    );
  }

  const active = data ? data.submissions.filter((s) => !s.archived) : [];
  const archived = data ? data.submissions.filter((s) => s.archived) : [];
  const visible = tab === "active" ? active : archived;

  const tabBtn = (key, label, count) => (
    <button
      onClick={() => setTab(key)}
      data-testid={`admin-tab-${key}`}
      className={`text-xs tracking-[0.25em] uppercase px-5 py-2.5 border transition-colors duration-300 ${
        tab === key
          ? "border-[#D4AF37] text-[#D4AF37]"
          : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
      }`}
    >
      {label} ({count})
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-testid="admin-submissions-page">
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoImage className="h-9 w-auto" />
            <span className="text-[0.55rem] tracking-[0.45em] text-white uppercase self-center">Interest Submissions</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="hidden sm:block text-xs text-white" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={signOut}
              data-testid="admin-logout-btn"
              className="text-xs tracking-[0.25em] uppercase border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-serif-display text-4xl sm:text-5xl" data-testid="admin-submissions-heading">
            Expressions of <em className="italic text-[#D4AF37]">interest</em>
          </h1>
          <button
            onClick={exportCsv}
            data-testid="admin-export-btn"
            className="text-xs tracking-[0.25em] uppercase border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
          >
            Download spreadsheet
          </button>
        </div>

        {data && (
          <div className="flex gap-3 mb-8" data-testid="admin-tabs">
            {tabBtn("active", "Active", active.length)}
            {tabBtn("archived", "Archived", archived.length)}
          </div>
        )}

        {error && <p className="text-sm text-red-400" data-testid="admin-submissions-error">{error}</p>}

        {!data && !error && (
          <p className="text-sm text-white animate-pulse" data-testid="admin-submissions-loading">Loading submissions…</p>
        )}

        {data && visible.length === 0 && (
          <div className="border border-white/10 bg-[#0E0E0E] p-16 text-center" data-testid="admin-submissions-empty">
            <p className="font-serif-display italic text-2xl text-white">
              {tab === "active" ? "No submissions yet." : "No archived submissions."}
            </p>
            <p className="text-sm text-[#5b5b63] mt-3">
              {tab === "active"
                ? "Expressions of interest will appear here as they arrive."
                : "Archived submissions will appear here."}
            </p>
          </div>
        )}

        {data && visible.length > 0 && (
          <div className="overflow-x-auto border border-white/10" data-testid="admin-submissions-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#141414] text-left">
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Received</th>
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Name</th>
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Email</th>
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Role</th>
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Message</th>
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Status</th>
                  <th className="px-6 py-4 text-[0.65rem] tracking-[0.25em] uppercase text-[#D4AF37] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s, i) => (
                  <tr key={s.id} className={`border-t border-white/10 hover:bg-white/[0.03] transition-colors duration-200 ${s.contacted ? "opacity-60" : ""}`} data-testid={`admin-submission-row-${i}`}>
                    <td className="px-6 py-4 text-white whitespace-nowrap">
                      {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-6 py-4 text-[#F5F5F5]">{s.name}</td>
                    <td className="px-6 py-4 text-white">{s.email}</td>
                    <td className="px-6 py-4 text-white">{s.role || "—"}</td>
                    <td className="px-6 py-4 text-white max-w-xs truncate" title={s.message || ""}>{s.message || "—"}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {s.contacted ? (
                        <span className="text-[#D4AF37] text-[0.65rem] tracking-[0.2em] uppercase" data-testid={`admin-submission-status-${i}`}>Contacted</span>
                      ) : (
                        <span className="text-white/60 text-[0.65rem] tracking-[0.2em] uppercase" data-testid={`admin-submission-status-${i}`}>New</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-3">
                        <button
                          onClick={() => toggleContacted(s)}
                          data-testid={`admin-submission-contacted-btn-${i}`}
                          className="text-[0.65rem] tracking-[0.15em] uppercase border border-[#D4AF37]/60 text-[#D4AF37] px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
                        >
                          {s.contacted ? "Mark new" : "Mark contacted"}
                        </button>
                        <button
                          onClick={() => toggleArchived(s)}
                          data-testid={`admin-submission-archive-btn-${i}`}
                          className="text-[0.65rem] tracking-[0.15em] uppercase border border-white/25 text-white px-3 py-1.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
                        >
                          {s.archived ? "Restore" : "Archive"}
                        </button>
                        <button
                          onClick={() => remove(s)}
                          data-testid={`admin-submission-delete-btn-${i}`}
                          className="text-[0.65rem] tracking-[0.15em] uppercase border border-white/20 text-white/70 px-3 py-1.5 hover:border-red-400 hover:text-red-400 transition-colors duration-300"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <ChangePasswordPanel />
      </main>
    </div>
  );
}
